import { Link } from "react-router-dom";
import {
    ArrowRight, ArrowUpRight, Sparkles, Palette, Truck, ShieldCheck, SprayCan, Car,
    Gift, Package, CreditCard, MessageSquare, Star, CheckCircle2, Type, Wand2,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import DeliveryCountdown from "../components/DeliveryCountdown";
import ReviewsStrip from "../components/ReviewsStrip";

const FALLBACK_BANNER = "★ STICKERS OP MAAT ★ TEGELTJES MET EIGEN TEKST ★ CADEAUBONNEN ★ PAKKETTEN ★ METAMARK M4 VINYL ★ MAT ★ GLANZEND ★";

export default function LandingPage() {
    const [banner, setBanner] = useState({ banner_text: FALLBACK_BANNER, banner_active: true });
    const [stickers, setStickers] = useState([]);
    const [bundles, setBundles] = useState([]);
    const [gifts, setGifts] = useState([]);
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        api.get("/site/banner").then(({ data }) => setBanner(data)).catch(() => {});
        api.get("/stickers").then(({ data }) => setStickers((data || []).filter(s => s.for_sale).slice(0, 4))).catch(() => {});
        api.get("/bundles").then(({ data }) => setBundles((data || []).slice(0, 3))).catch(() => {});
        api.get("/gift-products").then(({ data }) => setGifts((data || []).slice(0, 4))).catch(() => {});
        api.get("/site/reviews").then(({ data }) => setReviews((data || []).slice(0, 6))).catch(() => {});
    }, []);

    return (
        <div data-testid="landing-page" className="bg-zinc-50 text-zinc-950">
            {/* ───────── BANNER TICKER (boven hero) ───────── */}
            {banner.banner_active && (
                <div data-testid="promo-banner" className="border-b border-zinc-200 bg-zinc-950 text-zinc-50 py-3 ticker">
                    <div className="ticker-track text-sm font-mono-dm">
                        {Array(2).fill(0).map((_, i) => (
                            <span key={i}>{banner.banner_text}&nbsp;&nbsp;</span>
                        ))}
                    </div>
                </div>
            )}

            {/* ───────── HERO ───────── */}
            <section className="relative overflow-hidden hero-warm">
                <div className="absolute -top-32 -right-20 w-[32rem] h-[32rem] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(216,132,114,0.18), transparent)" }} />
                <div className="absolute top-60 -left-32 w-[24rem] h-[24rem] rounded-full pointer-events-none" style={{ background: "radial-gradient(closest-side, rgba(201,168,118,0.18), transparent)" }} />

                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 py-16 lg:py-24">
                    <div className="lg:col-span-6 flex flex-col justify-center">
                        <span className="label-tiny mb-5 inline-flex items-center gap-2" data-testid="hero-eyebrow">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--rose)" }} />
                            VdS Gift Shop · Stickers &amp; Tegeltjes
                        </span>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            Stickers op maat.<br />
                            <span style={{ color: "var(--rose-deep)" }}>Tegeltjes</span> met<br />
                            <span className="font-script text-5xl sm:text-6xl lg:text-7xl" style={{ color: "var(--gold)" }}>jouw woorden.</span>
                        </h1>
                        <p className="mt-7 text-base lg:text-lg max-w-xl leading-relaxed" style={{ color: "var(--text-2)" }}>
                            Hoogwaardige vinylstickers voor auto, ruit, helm of bedrijf — Metamark M4 mat
                            of M7 glanzend. Plus keramische tegeltjes 15×15&nbsp;cm met je eigen spreuk.
                            Snel gesneden in ons atelier in Sint-Niklaas.
                        </p>
                        <div className="mt-3 inline-flex items-start gap-2 px-3 py-2 rounded-xl max-w-xl" style={{ background: "var(--rose-tint)" }} data-testid="hero-mono-notice">
                            <span className="text-base shrink-0" aria-hidden>🎨</span>
                            <p className="text-xs leading-relaxed" style={{ color: "var(--text)" }}>
                                <strong>Mono vinyl:</strong> stickers &amp; logo&apos;s worden gesneden in één enkele kleur naar keuze — geen full-colour print.
                            </p>
                        </div>
                        <div className="mt-9 flex flex-wrap gap-3 items-center">
                            <Link to="/stickers" data-testid="hero-shop-cta">
                                <span className="btn-rose inline-flex items-center gap-2 px-7 py-4 text-sm">
                                    <Sparkles size={16} /> Shop stickers <ArrowRight size={16} />
                                </span>
                            </Link>
                            <Link to="/tegeltjes" data-testid="hero-tegel-cta">
                                <span className="btn-ghost inline-flex items-center gap-2 px-6 py-4 text-sm">
                                    Ontwerp je tegeltje
                                </span>
                            </Link>
                        </div>

                        <div className="mt-5">
                            <DeliveryCountdown />
                        </div>

                        {/* Mini USPs */}
                        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs" style={{ color: "var(--text-2)" }}>
                            <span className="inline-flex items-center gap-1.5" data-testid="hero-usp-1"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Metamark M4 &amp; M7 vinyl</span>
                            <span className="inline-flex items-center gap-1.5" data-testid="hero-usp-2"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Restloos verwijderbaar</span>
                            <span className="inline-flex items-center gap-1.5" data-testid="hero-usp-3"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Verzending in 3-7 dagen</span>
                            <span className="inline-flex items-center gap-1.5" data-testid="hero-usp-4"><CheckCircle2 size={13} style={{ color: "var(--rose)" }} /> Gemaakt in Sint-Niklaas</span>
                        </div>
                    </div>

                    {/* Right: typographic slogan poster (kleane, vervangt drukke auto-foto) */}
                    <div className="lg:col-span-6 relative min-h-[480px] flex items-center justify-center">
                        {/* Slogan poster */}
                        <div className="relative w-full max-w-lg aspect-[4/5] rounded-[2.5rem] shadow-[0_24px_64px_rgba(62,39,35,0.16)] flex flex-col items-center justify-center text-center p-10"
                            style={{ background: "linear-gradient(180deg, #FAE6DE 0%, #F5EDE0 100%)" }}>
                            {/* Subtle dotted texture */}
                            <div className="absolute inset-0 opacity-30 pointer-events-none rounded-[2.5rem] overflow-hidden" style={{
                                backgroundImage: "radial-gradient(circle, rgba(184,96,76,0.18) 1px, transparent 1px)",
                                backgroundSize: "20px 20px"
                            }} />

                            <p className="label-tiny relative" style={{ color: "var(--rose-deep)" }}>★ Atelier Sint-Niklaas</p>

                            <h2 className="font-display text-5xl md:text-6xl mt-6 leading-[0.95] relative" style={{ color: "var(--text)" }}>
                                Made
                                <span className="block font-script text-7xl md:text-8xl mt-1" style={{ color: "var(--rose-deep)" }}>with love</span>
                                <span className="block mt-1">since 2019</span>
                            </h2>

                            <div className="mt-8 flex items-center gap-3 relative">
                                <div className="h-px w-12" style={{ background: "var(--rose-deep)" }} />
                                <span className="font-mono-dm text-[10px] uppercase tracking-[0.25em]" style={{ color: "var(--text-2)" }}>Vinyl · keramiek · glas</span>
                                <div className="h-px w-12" style={{ background: "var(--rose-deep)" }} />
                            </div>

                            {/* Wax-seal style stamp — vergroot voor meer aanwezigheid */}
                            <div className="absolute -bottom-7 -right-7 w-36 h-36 rounded-full flex items-center justify-center rotate-[-12deg] shadow-[0_12px_28px_rgba(184,96,76,0.35)] border-4 border-white"
                                style={{ background: "radial-gradient(circle at 30% 30%, #E9A287 0%, #B8604C 60%, #8C4636 100%)", color: "white" }}
                                data-testid="hero-wax-seal">
                                <div className="text-center leading-tight">
                                    <p className="font-script text-3xl drop-shadow-sm">VdS</p>
                                    <p className="text-[9px] uppercase tracking-[0.18em] mt-1 font-semibold">Sint-Niklaas</p>
                                    <div className="flex items-center justify-center gap-1 mt-1">
                                        <span className="text-[8px]">★</span>
                                        <span className="text-[9px] font-mono-dm">2019</span>
                                        <span className="text-[8px]">★</span>
                                    </div>
                                </div>
                            </div>

                            {/* Small decorative sticker icons */}
                            <span className="absolute top-6 right-6 text-2xl opacity-60 rotate-12">★</span>
                            <span className="absolute bottom-10 left-8 text-2xl opacity-50 -rotate-12" style={{ color: "var(--gold)" }}>✦</span>
                        </div>

                        {/* Floating: M4 vs M7 badge */}
                        <div className="absolute top-4 left-0 lg:-left-4 bg-white/95 backdrop-blur px-4 py-3 rounded-2xl shadow-[0_8px_24px_rgba(62,39,35,0.12)] gentle-float z-10" data-testid="hero-floating-tag">
                            <p className="font-script text-xl leading-tight" style={{ color: "var(--rose-deep)" }}>Metamark M4 · M7</p>
                            <p className="label-tiny mt-0.5">M4 mat · M7 glanzend op bestelling</p>
                        </div>

                        {/* Floating tile preview */}
                        <div className="absolute bottom-4 right-0 lg:-right-2 bg-white/95 backdrop-blur px-5 py-4 rounded-2xl shadow-[0_8px_24px_rgba(62,39,35,0.12)] z-10" data-testid="hero-floating-tile">
                            <span className="label-tiny" style={{ color: "var(--rose-deep)" }}>Tegeltje 15×15</span>
                            <p className="font-display text-2xl mt-1 italic">&ldquo;Welkom thuis&rdquo;</p>
                            <p className="text-[10px] font-mono-dm tracking-widest mt-1" style={{ color: "var(--text-3)" }}>vanaf € 14,95</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ───────── USP BAR ───────── */}
            <section className="border-b border-zinc-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <Usp icon={<Palette size={18} strokeWidth={1.5} />} label="Metamark M4 vinyl" />
                    <Usp icon={<Truck size={18} strokeWidth={1.5} />} label="Verzending in 3-7 dagen" />
                    <Usp icon={<ShieldCheck size={18} strokeWidth={1.5} />} label="Made in Sint-Niklaas" />
                    <Usp icon={<Gift size={18} strokeWidth={1.5} />} label="Cadeau-pakketten op maat" />
                </div>
            </section>

            {/* ───────── BROWSE CATEGORIEËN: stickers & tegeltjes ───────── */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                <div className="text-center max-w-2xl mx-auto mb-12">
                    <p className="label-tiny" style={{ color: "var(--rose-deep)" }}>★ Browse de winkel</p>
                    <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                        Voor elke ondergrond, <span className="font-script" style={{ color: "var(--rose-deep)" }}>elke stijl.</span>
                    </h2>
                    <p className="mt-4" style={{ color: "var(--text-2)" }}>
                        Vier ingangen om snel te vinden wat je zoekt — stickers voor je auto, gevel of bedrijf, of een tegeltje met je eigen woorden.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <GiftEntry
                        to="/stickers#auto"
                        tag="Auto &amp; ruit"
                        title="Carrosserie · ramen · helmen · motoren"
                        emoji="🚗"
                        bg="rose-tint"
                        testId="cat-auto"
                    />
                    <GiftEntry
                        to="/tegeltjes"
                        tag="Tegeltjes"
                        title="Keramisch 15×15 cm met eigen tekst &amp; standaard"
                        emoji="🪧"
                        bg="gold-soft"
                        testId="cat-tegeltjes"
                    />
                    <GiftEntry
                        to="/maak-voor-mij"
                        tag="Op maat"
                        title="Eigen logo, naam of bedrijfssticker — wij snijden"
                        emoji="✂️"
                        bg="bg-warm"
                        testId="cat-op-maat"
                    />
                    <GiftEntry
                        to="/stickers#accessoires"
                        tag="Accessoires"
                        title="Transfertape · reiniger · applicatie-spray"
                        emoji="🧴"
                        bg="rose-tint"
                        testId="cat-accessoires"
                    />
                </div>
            </section>

            <ReviewsStrip />

            {/* ───────── TEGEL HIGHLIGHT ───────── */}
            <section className="border-y border-zinc-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-5">
                        <span className="label-tiny text-emerald-700">★ Nieuw in het assortiment</span>
                        <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                            Een tegeltje met<br/>
                            <em className="text-emerald-700 not-italic font-normal italic">jouw woorden</em>.
                        </h2>
                        <p className="mt-5 text-zinc-600 leading-relaxed max-w-md">
                            Keramisch tegeltje van 15×15 cm — inclusief stijlvolle standaard.
                            Kies een tegelkleur, tekstkleur en lettertype. Schrijf je eigen
                            spreuk, naam of liefdesverklaring. Wij printen, jij geeft cadeau.
                        </p>
                        <div className="mt-7 flex flex-wrap gap-3 items-center">
                            <Link to="/tegel-designer" data-testid="tile-cta-primary" className="btn-primary inline-flex items-center gap-2">
                                Ontwerp je tegeltje <ArrowRight size={14} />
                            </Link>
                            <span className="text-sm font-mono-dm">vanaf € 14,95</span>
                        </div>
                        <div className="mt-8 grid grid-cols-3 gap-2 max-w-sm">
                            <Bullet>20 tegelkleuren</Bullet>
                            <Bullet>16 tekstkleuren</Bullet>
                            <Bullet>9 lettertypen</Bullet>
                        </div>
                    </div>

                    {/* Mini-grid van tegel-previews */}
                    <div className="lg:col-span-7">
                        <div className="grid grid-cols-3 gap-3">
                            <TilePreview bg="#FFFFFF" border="#E4E4E7" tc="#0A0A0A" font="'Playfair Display', serif" text={`"Welkom\nthuis"`} italic />
                            <TilePreview bg="#0A0A0A" border="#0A0A0A" tc="#D4AF37" font="'Bebas Neue', sans-serif" text={`FAMILIE\nIS ALLES`} />
                            <TilePreview bg="#86EFAC" border="#16A34A" tc="#14532D" font="'Pacifico', cursive" text={`Lieve\nMama`} />
                            <TilePreview bg="#7C3AED" border="#581C87" tc="#FFFFFF" font="'Anton', sans-serif" text={`BEST\nDAD`} />
                            <TilePreview bg="#FBCFE8" border="#F9A8D4" tc="#9D174D" font="'Permanent Marker', cursive" text={`Sara\n& Tom`} />
                            <TilePreview bg="#FACC15" border="#CA8A04" tc="#0A0A0A" font="'Montserrat', sans-serif" text={`HAPPY\nDAYS`} bold />
                        </div>
                        <p className="label-tiny mt-4 text-zinc-400 text-right">Voorbeelden — jij maakt de jouwe</p>
                    </div>
                </div>
            </section>

            {/* ───────── BESTSELLERS / STICKERS ───────── */}
            {stickers.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                    <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                        <div>
                            <p className="label-tiny text-rose-600">★ Catalogus</p>
                            <h2 className="font-display text-4xl md:text-5xl mt-2 leading-none">
                                Klaar te plakken.
                            </h2>
                            <p className="text-zinc-500 mt-3 max-w-md">Selectie uit de catalogus — alle designs zijn op maat te bestellen in jouw kleur.</p>
                        </div>
                        <Link to="/stickers" className="btn-ghost inline-flex items-center gap-2 text-xs" data-testid="bestsellers-view-all">
                            Bekijk alle stickers <ArrowUpRight size={14} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {stickers.map(s => (
                            <Link
                                key={s.id}
                                to="/stickers"
                                data-testid={`landing-sticker-${s.id}`}
                                className="group border border-zinc-200 bg-white aspect-square flex flex-col"
                            >
                                <div className="flex-1 flex items-center justify-center p-6 text-zinc-950 group-hover:scale-105 transition-transform"
                                    dangerouslySetInnerHTML={{ __html: s.svg || "" }} />
                                <div className="border-t border-zinc-200 p-3 flex items-baseline justify-between">
                                    <span className="text-xs font-semibold uppercase tracking-wide truncate">{s.name}</span>
                                    <span className="text-[10px] font-mono-dm text-zinc-500 shrink-0 ml-2">{s.category}</span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* ───────── BUNDELS + GIFTS (2-kolom) ───────── */}
            <section className="border-y border-zinc-200 bg-white">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    {/* Bundels */}
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <p className="label-tiny text-rose-600">★ Pakketten</p>
                                <h3 className="font-display text-3xl md:text-4xl mt-1">Alles in één doos.</h3>
                            </div>
                            <Link to="/stickers#pakketten" className="text-xs font-bold uppercase tracking-widest underline hover:no-underline" data-testid="bundles-view-all">
                                Alle pakketten →
                            </Link>
                        </div>
                        {bundles.length > 0 ? (
                            <div className="space-y-3">
                                {bundles.map(b => (
                                    <div key={b.id} className="border border-zinc-200 p-5 flex items-center gap-4 hover:border-zinc-950 transition-colors">
                                        <div className="w-16 h-16 bg-rose-50 border border-rose-100 flex items-center justify-center shrink-0">
                                            <Package size={22} className="text-rose-600" strokeWidth={1.5} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold truncate">{b.name}</p>
                                            <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{b.description}</p>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <p className="font-mono-dm font-bold">€ {(b.final_price || 0).toFixed(2)}</p>
                                            {b.discount_percentage > 0 && (
                                                <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest">-{b.discount_percentage}%</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyHint icon={<Package size={20} />} text="Pakketten worden binnenkort toegevoegd." />
                        )}
                    </div>

                    {/* Cadeau producten */}
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <p className="label-tiny text-rose-600">★ Cadeau-ideeën</p>
                                <h3 className="font-display text-3xl md:text-4xl mt-1">Cadeau-klaar.</h3>
                            </div>
                            <Link to="/stickers#cadeaus" className="text-xs font-bold uppercase tracking-widest underline hover:no-underline" data-testid="gifts-view-all">
                                Alle cadeaus →
                            </Link>
                        </div>
                        {gifts.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3">
                                {gifts.map(g => (
                                    <div key={g.id} className="border border-zinc-200 bg-zinc-50 overflow-hidden">
                                        <div className="aspect-square bg-zinc-100 overflow-hidden">
                                            {g.image_url ? (
                                                <img src={g.image_url} alt={g.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-rose-300">
                                                    <Gift size={32} strokeWidth={1} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-3 bg-white border-t border-zinc-200">
                                            <p className="text-xs font-semibold truncate">{g.name}</p>
                                            <p className="text-[10px] font-mono-dm text-zinc-500 mt-0.5">€ {(g.price || 0).toFixed(2)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <EmptyHint icon={<Gift size={20} />} text="Cadeau-producten worden binnenkort getoond." />
                        )}
                    </div>
                </div>
            </section>

            {/* ───────── HOE HET WERKT ───────── */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                <div className="text-center mb-12">
                    <p className="label-tiny text-rose-600">★ Hoe het werkt</p>
                    <h2 className="font-display text-4xl md:text-5xl mt-2">In 3 stappen jouw cadeau.</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-200 bg-white">
                    <HowStep n="01" title="Ontwerp" desc="Kies een sticker, ontwerp er zelf eentje of personaliseer je tegeltje met eigen tekst." icon={<Wand2 size={22} strokeWidth={1.5} />} />
                    <HowStep n="02" title="Wij produceren" desc="Ons atelier snijdt, drukt en pakt zorgvuldig in — 3 tot 7 werkdagen levertijd." icon={<SprayCan size={22} strokeWidth={1.5} />} borderLeft />
                    <HowStep n="03" title="Bezorgd aan huis" desc="Cadeau-waardig verpakt, klaar om te plakken of weg te geven. Eenvoudig." icon={<Truck size={22} strokeWidth={1.5} />} borderLeft />
                </div>
            </section>

            {/* ───────── REVIEWS ───────── */}
            {reviews.length > 0 && (
                <section id="reviews" className="border-y border-zinc-200 bg-zinc-950 text-white">
                    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
                            <div>
                                <p className="label-tiny text-rose-400">★ Wat klanten zeggen</p>
                                <h2 className="font-display text-4xl md:text-5xl mt-2 leading-none">
                                    4,8 / 5 — en daar zijn we trots op.
                                </h2>
                            </div>
                            <div className="flex items-center gap-1.5">
                                {[0,1,2,3,4].map(i => (
                                    <svg key={i} width="20" height="20" viewBox="0 0 24 24" fill="#FACC15">
                                        <path d="M12 2l2.99 6.06 6.69.97-4.84 4.71 1.14 6.66L12 17.27l-5.98 3.14 1.14-6.66L2.32 9.03l6.69-.97L12 2z"/>
                                    </svg>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-reviews-grid">
                            {reviews.map(r => (
                                <div key={r.id} className="border border-zinc-800 bg-zinc-900 p-6 flex flex-col">
                                    <div className="flex items-center gap-1 mb-4">
                                        {Array(r.rating || 5).fill(0).map((_, i) => (
                                            <svg key={i} width="13" height="13" viewBox="0 0 24 24" fill="#FACC15">
                                                <path d="M12 2l2.99 6.06 6.69.97-4.84 4.71 1.14 6.66L12 17.27l-5.98 3.14 1.14-6.66L2.32 9.03l6.69-.97L12 2z"/>
                                            </svg>
                                        ))}
                                    </div>
                                    <p className="text-base leading-relaxed flex-1">&ldquo;{r.quote}&rdquo;</p>
                                    <div className="mt-5 pt-4 border-t border-zinc-800 flex items-baseline justify-between text-xs">
                                        <div>
                                            <p className="font-bold">{r.author}</p>
                                            <p className="text-zinc-500 font-mono-dm text-[10px] uppercase tracking-widest mt-1">{r.location}</p>
                                        </div>
                                        {r.verified && (
                                            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400 uppercase tracking-widest font-bold">
                                                <CheckCircle2 size={12} /> Geverifieerd
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* ───────── CTA FOOTER ───────── */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
                <div className="border border-zinc-200 bg-white p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <p className="label-tiny text-rose-600">★ Op zoek naar iets speciaals?</p>
                        <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
                            Stickers op maat?<br />
                            <span className="text-rose-600">Vraag offerte.</span>
                        </h2>
                        <p className="mt-5 text-zinc-600 leading-relaxed max-w-md">
                            Eigen logo, voertuig-belettering, bedrijfsstickers of grote oplages?
                            We snijden en leveren op maat — vraag vrijblijvend een offerte.
                        </p>
                    </div>
                    <div className="lg:col-span-5 flex flex-col gap-3 items-stretch">
                        <Link to="/contact" data-testid="footer-cta-quote" className="btn-primary inline-flex items-center justify-center gap-2 py-4">
                            <MessageSquare size={14} /> Offerte aanvragen
                        </Link>
                        <Link to="/tegel-designer" data-testid="footer-cta-tegel" className="btn-ghost inline-flex items-center justify-center gap-2 py-4">
                            <Sparkles size={14} /> Of ontwerp een tegeltje
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

/* ───── small sub-components ───── */

function Stat({ n, label }) {
    return (
        <div>
            <p className="font-display text-2xl md:text-3xl">{n}</p>
            <p className="label-tiny mt-1">{label}</p>
        </div>
    );
}

function Usp({ icon, label }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-zinc-200 flex items-center justify-center text-rose-600 shrink-0">
                {icon}
            </div>
            <p className="text-xs font-semibold uppercase tracking-wide">{label}</p>
        </div>
    );
}

function CategoryCard({ to, eyebrow, title, desc, accent, icon, badge }) {
    return (
        <Link
            to={to}
            data-testid={`cat-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
            className="group relative border border-zinc-200 bg-white p-6 flex flex-col min-h-[260px] hover:border-zinc-950 transition-colors"
        >
            {badge && (
                <span className="absolute -top-3 right-4 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                    {badge}
                </span>
            )}
            <div className={`w-12 h-12 ${accent} border border-zinc-200 flex items-center justify-center text-zinc-950`}>
                {icon}
            </div>
            <p className="label-tiny mt-5">{eyebrow}</p>
            <h3 className="font-display text-2xl mt-2 leading-tight">{title}</h3>
            <p className="text-sm text-zinc-500 mt-3 leading-relaxed flex-1">{desc}</p>
            <span className="mt-5 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-zinc-950 group-hover:text-rose-600 transition-colors">
                Ontdek <ArrowUpRight size={14} />
            </span>
        </Link>
    );
}

function TilePreview({ bg, border, tc, font, text, italic, bold }) {
    return (
        <div className="aspect-square border-2 flex items-center justify-center p-3 shadow-sm" style={{ background: bg, borderColor: border }}>
            <span
                className="text-center leading-tight whitespace-pre-line"
                style={{
                    fontFamily: font,
                    color: tc,
                    fontStyle: italic ? "italic" : "normal",
                    fontWeight: bold ? 700 : (font.includes("Anton") || font.includes("Bebas") ? 400 : 600),
                    fontSize: "clamp(11px, 2vw, 18px)",
                }}
            >
                {text}
            </span>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <div className="flex items-start gap-1.5 text-[11px] text-zinc-600">
            <CheckCircle2 size={12} className="text-emerald-600 shrink-0 mt-0.5" />
            <span>{children}</span>
        </div>
    );
}

function EmptyHint({ icon, text }) {
    return (
        <div className="border border-dashed border-zinc-300 p-6 text-zinc-500 text-sm flex items-center gap-3">
            <span className="text-zinc-400">{icon}</span>
            {text}
        </div>
    );
}

function HowStep({ n, title, desc, icon, borderLeft }) {
    return (
        <div className={`p-8 ${borderLeft ? "md:border-l md:border-zinc-200" : ""}`}>
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-600">{icon}</div>
                <p className="label-tiny">Stap {n}</p>
            </div>
            <h3 className="font-display text-2xl mt-4">{title}</h3>
            <p className="text-sm text-zinc-600 mt-3 leading-relaxed">{desc}</p>
        </div>
    );
}


function GiftEntry({ to, tag, title, emoji, bg, testId }) {
    const bgMap = {
        "rose-tint": "var(--rose-tint)",
        "gold-soft": "var(--gold-soft)",
        "bg-warm": "var(--bg-warm)",
    };
    return (
        <Link
            to={to}
            data-testid={testId}
            className="group relative rounded-3xl p-7 flex flex-col min-h-[220px] transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(62,39,35,0.10)]"
            style={{ background: bgMap[bg] || "var(--rose-tint)" }}
        >
            <div className="text-4xl" aria-hidden>{emoji}</div>
            <p className="label-tiny mt-4" style={{ color: "var(--rose-deep)" }}>{tag}</p>
            <p className="font-display text-xl mt-2 leading-snug flex-1" style={{ color: "var(--text)" }}>{title}</p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "var(--text)" }}>
                Bekijk <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
        </Link>
    );
}
